import React, { useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Image,
} from 'react-native';
import { Invitation } from '@/types/residents/home';
import { LINKED_ACCOUNTS_COLORS } from '@/constants/residents/mobile/linkedAccountsColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { getDisplayName, getInitials } from '@/utils/residents/utils';
import { formatInvitationDate, hasPassedHours } from '@/utils/residents/date';
import { invitationsStrings } from '@/constants/residents/strings';

const PersonIcon = RESIDENTS_SVG_COMPONENTS.USER;
const RefreshIcon = RESIDENTS_SVG_COMPONENTS.REFRESH;

interface MobileInvitationCardProps {
  invitation: Invitation;
  onAccept?: (invitation: Invitation) => void;
  onDecline?: (invitation: Invitation) => void;
  onCancel?: (invitation: Invitation) => void;
  onResend?: (invitation: Invitation) => void;
}

/**
 * Mobile Invitation Card Component
 * Matches Figma design for received and sent invitations
 * 
 * Received invitations: Accept/Decline actions
 * Sent invitations: Resend/Cancel actions
 */
export const MobileInvitationCard: React.FC<MobileInvitationCardProps> = ({
  invitation,
  onAccept,
  onDecline,
  onCancel,
  onResend,
}) => {
  const { user, sender, createdAt } = invitation;
  const displayName = getDisplayName(user.firstName, user.lastName);
  const hasName = !!displayName;
  const displayText = hasName ? displayName : user.email;

  // Get initials for avatar using shared utility
  const initials = useMemo(() => {
    return getInitials(user.firstName, user.lastName);
  }, [user.firstName, user.lastName]);

  // Format date for display using shared utility
  const formattedDate = useMemo(() => {
    return formatInvitationDate(createdAt);
  }, [createdAt]);

  // Check if resend should be active (24 hours have passed since invitation was sent)
  const isResendActive = useMemo(() => {
    return hasPassedHours(createdAt, 24);
  }, [createdAt]);

  // Status text based on sender flag
  const statusText = sender
    ? invitationsStrings.cell.didInvite
    : invitationsStrings.cell.wasInvited;

  // Handle button presses
  const handleAccept = () => onAccept?.(invitation);
  const handleDecline = () => onDecline?.(invitation);
  const handleCancel = () => onCancel?.(invitation);
  const handleResend = () => {
    if (isResendActive) {
      onResend?.(invitation);
    }
  };

  return (
    <View style={styles.container}>
      {/* Avatar */}
      <View style={styles.avatarContainer}>
        {user.photoUrl ? (
          <Image
            source={{ uri: user.photoUrl }}
            style={styles.avatarImage}
            resizeMode="cover"
          />
        ) : initials ? (
          <Text style={styles.avatarInitials}>{initials}</Text>
        ) : (
          <PersonIcon width={24} height={24} color={LINKED_ACCOUNTS_COLORS.textPrimary} />
        )}
      </View>

      {/* Content */}
      <View style={styles.contentContainer}>
        {/* Text Content */}
        <View style={styles.textContent}>
          {/* Top section: Name and Status */}
          <View style={styles.topSection}>
            <Text style={styles.nameText} numberOfLines={1}>
              {displayText}
            </Text>
            <Text style={styles.statusText}>{statusText}</Text>
          </View>

          {/* Bottom section: Date */}
          <Text style={styles.dateText}>
            {invitationsStrings.cell.invitedOn} {formattedDate}
          </Text>
        </View>

        {/* Action Buttons */}
        <View style={styles.actionsContainer}>
          {sender ? (
            // Sent invitation actions
            <>
              <TouchableOpacity
                style={[
                  styles.resendButton,
                  !isResendActive && styles.resendButtonInactive,
                ]}
                onPress={handleResend}
                activeOpacity={isResendActive ? 0.8 : 1}
                disabled={!isResendActive}
              >
                <RefreshIcon 
                  width={16} 
                  height={16} 
                  color={isResendActive ? LINKED_ACCOUNTS_COLORS.background : LINKED_ACCOUNTS_COLORS.textSecondary} 
                />
                <Text
                  style={[
                    styles.resendButtonText,
                    !isResendActive && styles.resendButtonTextInactive,
                  ]}
                >
                  {invitationsStrings.cell.resend}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.secondaryAction}
                onPress={handleCancel}
                activeOpacity={0.7}
              >
                <Text style={styles.secondaryActionText}>
                  {invitationsStrings.cell.cancelTitle}
                </Text>
              </TouchableOpacity>
            </>
          ) : (
            // Received invitation actions
            <>
              <TouchableOpacity
                style={styles.acceptButton}
                onPress={handleAccept}
                activeOpacity={0.8}
              >
                <Text style={styles.acceptButtonText}>
                  {invitationsStrings.cell.ctaTitle}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.secondaryAction}
                onPress={handleDecline}
                activeOpacity={0.7}
              >
                <Text style={styles.declineText}>
                  {invitationsStrings.cell.declineTitle}
                </Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: LINKED_ACCOUNTS_COLORS.cardBackground,
    borderRadius: 16,
    padding: 20,
    flexDirection: 'row',
    gap: 16,
  },
  // Avatar
  avatarContainer: {
    width: 68,
    height: 68,
    borderRadius: 34,
    backgroundColor: LINKED_ACCOUNTS_COLORS.avatarBackground,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 34,
  },
  avatarInitials: {
    fontSize: 24,
    fontWeight: '700',
    color: LINKED_ACCOUNTS_COLORS.textPrimary,
    lineHeight: 36,
  },
  // Content
  contentContainer: {
    flex: 1,
    gap: 20,
  },
  textContent: {
    gap: 8,
  },
  topSection: {
    gap: 2,
  },
  nameText: {
    fontSize: 18,
    fontWeight: '600',
    color: LINKED_ACCOUNTS_COLORS.textPrimary,
    lineHeight: 27,
  },
  statusText: {
    fontSize: 14,
    fontWeight: '400',
    color: LINKED_ACCOUNTS_COLORS.textPrimary,
    lineHeight: 21,
  },
  dateText: {
    fontSize: 12,
    fontWeight: '300',
    color: LINKED_ACCOUNTS_COLORS.textSecondary,
    lineHeight: 18,
  },
  // Actions
  actionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
  },
  acceptButton: {
    backgroundColor: LINKED_ACCOUNTS_COLORS.accentPink,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    height: 34,
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 131,
  },
  acceptButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: LINKED_ACCOUNTS_COLORS.background, // Dark text on pink button
    lineHeight: 18,
  },
  resendButton: {
    backgroundColor: LINKED_ACCOUNTS_COLORS.accentPink,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    height: 34,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    minWidth: 100,
  },
  resendButtonInactive: {
    backgroundColor: LINKED_ACCOUNTS_COLORS.textMuted,
  },
  resendButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: LINKED_ACCOUNTS_COLORS.background,
    lineHeight: 18,
  },
  resendButtonTextInactive: {
    color: LINKED_ACCOUNTS_COLORS.textSecondary,
  },
  secondaryAction: {
    height: 34,
    justifyContent: 'center',
    alignItems: 'center',
  },
  secondaryActionText: {
    fontSize: 12,
    fontWeight: '500',
    color: LINKED_ACCOUNTS_COLORS.accentPink,
    lineHeight: 18,
  },
  declineText: {
    fontSize: 12,
    fontWeight: '500',
    color: LINKED_ACCOUNTS_COLORS.accentPink,
    lineHeight: 18,
  },
});

